/**
 * DeCarbonIQ — Landing Page
 * 7 sections: Hero → Flow → Personas → Capabilities → Registry → Article 6 → CTA
 *
 * Design: Dark navy (#0F172A) + teal (#059669). Minimal text. Action-oriented.
 * "If someone needs 2 minutes to understand → you've lost them."
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, BarChart2, Zap, Layers, DollarSign, ShieldCheck,
  Building2, BarChart3, GitBranch, Landmark, Globe, ChevronRight,
  Menu, X, Hash, FileCheck, CheckCircle2
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";
const SOLAR_URL = "https://images.pexels.com/photos/6996421/pexels-photo-6996421.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// ── Navigation ─────────────────────────────────────────────────────────────
function Nav() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <nav className="bg-[#0F172A] border-b border-white/8 sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <img src={LOGO_URL} alt="DeCarbonIQ" className="h-7 w-auto" style={{ filter: "brightness(0) invert(1)" }} />

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8 text-sm text-slate-400 font-medium">
          <a href="#platform" className="hover:text-white transition-colors">Platform</a>
          <a href="#for-who" className="hover:text-white transition-colors">For You</a>
          <a href="#registry" className="hover:text-white transition-colors">Registry</a>
          <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
        </div>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" className="text-sm text-slate-400 hover:text-white transition-colors">Sign in</Link>
          <button onClick={() => navigate("/get-started")}
            className="flex items-center gap-2 px-4 py-2 bg-[#059669] text-white text-sm font-semibold rounded-xl hover:bg-[#047857] transition-colors">
            Start Now <ArrowRight size={14} />
          </button>
        </div>

        {/* Mobile hamburger */}
        <button className="md:hidden text-slate-400 hover:text-white" onClick={() => setOpen(!open)}>
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden bg-[#0F172A] border-t border-white/8 px-6 py-4 space-y-3">
          {["Platform","For You","Registry","Pricing"].map(item => (
            <a key={item} href={`#${item.toLowerCase().replace(" ","-")}`}
              className="block text-sm text-slate-400 hover:text-white py-1 transition-colors"
              onClick={() => setOpen(false)}>{item}</a>
          ))}
          <div className="pt-2 flex flex-col gap-2">
            <Link to="/login" className="text-sm text-slate-400 text-center py-2 border border-slate-700 rounded-xl">Sign in</Link>
            <Link to="/get-started" className="text-sm text-white text-center py-2 bg-[#059669] rounded-xl font-semibold">Start Now</Link>
          </div>
        </div>
      )}
    </nav>
  );
}

// ── SECTION 1: HERO ────────────────────────────────────────────────────────
function Hero() {
  const navigate = useNavigate();
  return (
    <section className="relative bg-[#0F172A] overflow-hidden min-h-[88vh] flex items-center" data-testid="hero-section">
      {/* Background image overlay */}
      <div className="absolute inset-0">
        <img src={SOLAR_URL} alt="" className="w-full h-full object-cover opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/95 to-[#0F172A]/60" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 py-24 w-full">
        <div className="max-w-3xl">
          {/* Pre-headline badge */}
          <div className="inline-flex items-center gap-2 bg-[#059669]/15 border border-[#059669]/30 px-3 py-1.5 rounded-full mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#059669] animate-pulse" />
            <span className="text-[#059669] text-xs font-semibold tracking-wide">Climate Finance Infrastructure</span>
          </div>

          {/* Main headline */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-none tracking-tight mb-6">
            Measure.<br />
            Act.<br />
            <span className="text-[#059669]">Unlock Climate Value.</span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl text-slate-300 mb-10 max-w-xl leading-relaxed">
            Understand your impact. Take action. Unlock real value.
          </p>

          {/* CTAs */}
          <div className="flex items-center gap-4 flex-wrap">
            <button onClick={() => navigate("/get-started")}
              className="flex items-center gap-2 px-7 py-3.5 bg-[#059669] text-white font-bold rounded-xl hover:bg-[#047857] transition-all shadow-lg shadow-[#059669]/25 text-base"
              data-testid="hero-start-btn">
              Start Now <ArrowRight size={18} />
            </button>
            <Link to="/get-started"
              className="flex items-center gap-2 px-7 py-3.5 border border-white/20 text-white font-semibold rounded-xl hover:bg-white/8 transition-colors text-base">
              Request a Demo
            </Link>
          </div>

          {/* SME Quick Start shortcut */}
          <div className="mt-5 flex items-center gap-2">
            <span className="text-slate-500 text-sm">Running an SME?</span>
            <button
              onClick={() => navigate("/signup?role=sme")}
              className="text-sm text-[#059669] font-semibold hover:underline flex items-center gap-1"
              data-testid="sme-quick-start-btn">
              Quick Start for SMEs <ChevronRight size={13} />
            </button>
          </div>

          {/* Social proof strip */}
          <div className="mt-12 flex items-center gap-6 flex-wrap">
            {[
              ["GHG Protocol", "Aligned"],
              ["IFRS S2",      "Ready"],
              ["Article 6",   "Infrastructure"],
              ["VCM",         "Enabled"],
            ].map(([label, status]) => (
              <div key={label} className="flex items-center gap-1.5">
                <CheckCircle2 size={13} className="text-[#059669]" />
                <span className="text-xs text-slate-400">{label} <span className="text-slate-500">{status}</span></span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── SECTION 2: PLATFORM FLOW ──────────────────────────────────────────────
const FLOW_STEPS = [
  { n: "01", label: "Measure",   desc: "Track emissions across operations — Scope 1, 2, and 3.", icon: BarChart2,  color: "#059669" },
  { n: "02", label: "Act",       desc: "Identify your highest-impact opportunities. Build your action plan.", icon: Zap,       color: "#0891B2" },
  { n: "03", label: "Structure", desc: "Turn actions into credible climate projects with governance.", icon: Layers,    color: "#7C3AED" },
  { n: "04", label: "Finance",   desc: "Access capital with a data-backed financing application.", icon: DollarSign, color: "#D97706" },
  { n: "05", label: "Prove",     desc: "Generate verifiable outputs. Build institutional credibility.", icon: ShieldCheck, color: "#059669" },
];

function PlatformFlow() {
  return (
    <section id="platform" className="bg-white py-24" data-testid="platform-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-[#059669] text-xs font-bold uppercase tracking-widest mb-3">How It Works</p>
          <h2 className="text-4xl font-bold text-[#0F172A] tracking-tight">From Data to Decisions to Real Outcomes</h2>
        </div>

        {/* Flow steps */}
        <div className="relative">
          {/* Connector line */}
          <div className="hidden lg:block absolute top-10 left-0 right-0 h-px bg-slate-200 mx-[8%]" />

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {FLOW_STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={step.n} className="relative flex flex-col items-center text-center"
                  data-testid={`flow-step-${i+1}`}>
                  {/* Step circle */}
                  <div className="relative z-10 w-20 h-20 rounded-full bg-white border-2 border-slate-100 flex flex-col items-center justify-center mb-5 shadow-sm">
                    <Icon size={20} style={{ color: step.color }} />
                    <span className="text-[9px] text-slate-400 font-bold mt-0.5">{step.n}</span>
                  </div>
                  <p className="text-base font-bold text-[#0F172A] mb-2">{step.label}</p>
                  <p className="text-sm text-slate-400 leading-relaxed">{step.desc}</p>
                  {/* Arrow for mobile */}
                  {i < FLOW_STEPS.length - 1 && (
                    <ChevronRight size={16} className="text-slate-300 mt-4 lg:hidden mx-auto rotate-90 md:rotate-0" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── SECTION 3: WHO IT'S FOR ───────────────────────────────────────────────
const PERSONAS = [
  {
    id:       "sme",
    label:    "SME",
    title:    "Energy. Industrial. Distribution.",
    tagline:  "Measure costs. Act on savings. Access financing.",
    bullets:  ["Reduce operational costs", "Improve energy efficiency", "Access green financing"],
    icon:     Building2,
    color:    "#059669",
  },
  {
    id:       "corporate",
    label:    "Corporate",
    title:    "Groups. Multi-site. Supply chain.",
    tagline:  "Manage emissions. Disclose with confidence.",
    bullets:  ["Group-wide emissions tracking", "IFRS S2 / TCFD / CDP ready", "Build carbon project pipeline"],
    icon:     BarChart3,
    color:    "#0891B2",
  },
  {
    id:       "project",
    label:    "Project Developer",
    title:    "Climate Asset Infrastructure.",
    tagline:  "Structure projects. Build credible assets.",
    bullets:  ["Baseline and additionality tools", "MRV governance stack", "Article 6 structuring support"],
    icon:     GitBranch,
    color:    "#7C3AED",
  },
  {
    id:       "fi",
    label:    "Financial Institution",
    title:    "Banks. DFIs. Climate funds.",
    tagline:  "Evaluate deals. Finance credible projects.",
    bullets:  ["Structured financing pipeline", "Data-backed deal review", "Portfolio-level risk view"],
    icon:     Landmark,
    color:    "#D97706",
  },
];

function WhoItsFor() {
  const navigate = useNavigate();
  return (
    <section id="for-who" className="bg-[#0F172A] py-24" data-testid="personas-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-[#059669] text-xs font-bold uppercase tracking-widest mb-3">Who It's For</p>
          <h2 className="text-4xl font-bold text-white tracking-tight">Built for Every Actor in the Climate Value Chain</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PERSONAS.map(p => {
            const Icon = p.icon;
            return (
              <div key={p.id}
                className="group bg-white/5 border border-white/10 rounded-xl p-6 cursor-pointer hover:bg-white/10 hover:border-white/20 transition-all"
                onClick={() => navigate(`/get-started?role=${p.id}`)}
                data-testid={`persona-${p.id}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 flex-shrink-0"
                  style={{ backgroundColor: `${p.color}20` }}>
                  <Icon size={18} style={{ color: p.color }} />
                </div>
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: p.color }}>{p.label}</p>
                <p className="text-sm font-bold text-white mb-1.5">{p.title}</p>
                <p className="text-xs text-slate-400 mb-4 leading-relaxed">{p.tagline}</p>
                <ul className="space-y-1.5">
                  {p.bullets.map(b => (
                    <li key={b} className="flex items-start gap-2 text-xs text-slate-400">
                      <span style={{ color: p.color }} className="mt-0.5 flex-shrink-0">→</span>
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-5 flex items-center gap-1 text-xs font-semibold group-hover:gap-2 transition-all" style={{ color: p.color }}>
                  Get started <ArrowRight size={12} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── SECTION 4: CAPABILITIES ────────────────────────────────────────────────
const CAPABILITIES = [
  {
    icon:  BarChart2,
    title: "Measure & Track",
    items: ["Scope 1, 2, and 3 emissions", "Monthly operational tracking", "Sector-specific emission factors", "Benchmark vs industry peers"],
    color: "#059669",
  },
  {
    icon:  Zap,
    title: "Action & Optimization",
    items: ["Dynamic, data-driven opportunities", "Action plans with CAPEX + ROI", "Energy savings quantification", "Causal driver identification"],
    color: "#0891B2",
  },
  {
    icon:  Layers,
    title: "Project Structuring",
    items: ["Baseline vs. project analysis", "MRV governance framework", "Additionality assessment tools", "LoA and sovereign alignment"],
    color: "#7C3AED",
  },
  {
    icon:  DollarSign,
    title: "Climate Finance",
    items: ["Financing readiness scoring", "2-layer financing range model", "Direct FI deal submission", "Finance pack auto-generation"],
    color: "#D97706",
  },
  {
    icon:  ShieldCheck,
    title: "Credibility & Verification",
    items: ["Auditable calculation versions", "Attestation and chain of custody", "Publicly verifiable reports", "Institutional-grade exports"],
    color: "#059669",
  },
];

function Capabilities() {
  return (
    <section className="bg-[#F8FAFC] py-24" data-testid="capabilities-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-[#059669] text-xs font-bold uppercase tracking-widest mb-3">Platform</p>
          <h2 className="text-4xl font-bold text-[#0F172A] tracking-tight">A Complete Climate Infrastructure System</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {CAPABILITIES.map((cap, i) => {
            const Icon = cap.icon;
            return (
              <div key={cap.title}
                className={`bg-white border border-slate-200 rounded-xl p-6 hover:border-slate-300 hover:shadow-md transition-all ${i === 4 ? "md:col-span-2 lg:col-span-1" : ""}`}
                data-testid={`capability-${i+1}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${cap.color}15` }}>
                  <Icon size={18} style={{ color: cap.color }} />
                </div>
                <p className="text-sm font-semibold text-slate-900 mb-3">{cap.title}</p>
                <ul className="space-y-1.5">
                  {cap.items.map(item => (
                    <li key={item} className="flex items-start gap-2 text-xs text-slate-500">
                      <span style={{ color: cap.color }} className="mt-0.5 flex-shrink-0 font-bold">—</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── SECTION 5: PUBLIC REGISTRY ─────────────────────────────────────────────
function PublicRegistry() {
  const navigate = useNavigate();
  return (
    <section id="registry" className="bg-[#0F172A] py-24" data-testid="registry-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[#059669] text-xs font-bold uppercase tracking-widest mb-4">Public Registry</p>
            <h2 className="text-4xl font-bold text-white tracking-tight mb-5">Transparent. Verifiable. Trusted.</h2>
            <p className="text-slate-300 text-lg mb-8 leading-relaxed">
              Every report and certificate generated on the platform can be independently verified by anyone, anywhere.
            </p>
            <div className="space-y-3 mb-8">
              {[
                ["Public access", "No login required to verify any report"],
                ["Unique IDs",    "Every document has a tamper-evident unique identifier"],
                ["Audit trail",  "Full version history and chain of custody recorded"],
              ].map(([title, desc]) => (
                <div key={title} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#059669]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle2 size={11} className="text-[#059669]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{title}</p>
                    <p className="text-xs text-slate-400">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => navigate("/registry")}
              className="flex items-center gap-2 px-6 py-3 border border-[#059669]/40 text-[#059669] font-semibold rounded-xl hover:bg-[#059669]/10 transition-colors"
              data-testid="view-registry-btn">
              <Globe size={16} /> View Public Registry
            </button>
          </div>

          {/* Real Registry Entry — Gasavant Africa Ltd */}
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            {/* Card header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#059669] animate-pulse" />
                <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">Live Registry Entry</p>
              </div>
              <span className="text-[9px] font-bold text-teal-400 bg-teal-400/10 border border-teal-400/20 px-2 py-0.5 rounded-full">
                DCI-2026-0001
              </span>
            </div>

            {/* Data rows */}
            <div className="px-5 py-4 space-y-0">
              {[
                { label: "Document ID",         value: "DCI-2026-0001",                            mono: true },
                { label: "Type",                value: "Climate Credentials Report"                            },
                { label: "Organisation",        value: "Gasavant Africa Ltd (Nigeria)"                         },
                { label: "Issued",              value: "22 Mar 2026"                                           },
                { label: "Status",              value: "Assessed",                                 green: true },
                { label: "Reported tCO₂e",      value: "62.8 tCO₂e / FY2024",                     mono: true  },
                { label: "CRS",                 value: "55/100 (Band B)"                                       },
                { label: "Industry Position",   value: "More efficient than average",              teal: true  },
              ].map(row => (
                <div key={row.label} className="flex justify-between items-center py-2.5 border-b border-white/5 last:border-0">
                  <span className="text-[10px] text-slate-500">{row.label}</span>
                  <span className={`text-[11px] font-semibold ${
                    row.green ? "text-[#059669]" :
                    row.teal  ? "text-teal-400"  :
                                "text-white"
                  } ${row.mono ? "font-mono" : ""}`}>
                    {row.value}
                  </span>
                </div>
              ))}
            </div>

            {/* Footer — hash reference + CTA */}
            <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5 min-w-0">
                <Hash size={9} className="text-slate-600 flex-shrink-0" />
                <span className="text-[9px] font-mono text-slate-600 truncate">Ref: CCR-27-d4dbc18d</span>
              </div>
              <a
                href="/verify/report/CCR-27-d4dbc18d"
                className="flex-shrink-0 text-[10px] font-bold text-[#059669] hover:text-teal-400 transition-colors flex items-center gap-1"
                data-testid="registry-card-view-credentials">
                View Credentials →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── SECTION 6: ARTICLE 6 / PROJECTS ───────────────────────────────────────
function Article6Section() {
  return (
    <section className="bg-white py-24" data-testid="article6-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center">
          <p className="text-[#059669] text-xs font-bold uppercase tracking-widest mb-4">Climate Projects</p>
          <h2 className="text-4xl font-bold text-[#0F172A] tracking-tight mb-5">
            From Emissions to Structured Climate Projects
          </h2>
          <p className="text-slate-500 text-lg mb-10 leading-relaxed">
            DeCarbonIQ provides the infrastructure to assess, structure, and govern climate projects —
            from baseline establishment to institutional submission.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {[
            {
              title:    "Project Readiness Assessment",
              desc:     "Assess whether your emission reduction activities meet the structural criteria for formal climate project development.",
              icon:     FileCheck,
              color:    "#059669",
            },
            {
              title:    "Carbon Market Alignment",
              desc:     "Evaluate suitability for voluntary carbon markets. Projects may be suitable subject to independent additionality assessment and methodology validation.",
              icon:     GitBranch,
              color:    "#7C3AED",
            },
            {
              title:    "Sovereign & Article 6",
              desc:     "Support for Article 6 exploration. Eligibility is determined by host country governments and the UNFCCC — not by this platform.",
              icon:     Globe,
              color:    "#0891B2",
            },
          ].map(item => {
            const Icon = item.icon;
            return (
              <div key={item.title} className="bg-[#F8FAFC] border border-slate-200 rounded-xl p-6">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${item.color}15` }}>
                  <Icon size={16} style={{ color: item.color }} />
                </div>
                <p className="text-sm font-semibold text-slate-900 mb-2">{item.title}</p>
                <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Careful disclaimer */}
        <div className="text-center">
          <p className="text-xs text-slate-400 italic leading-relaxed">
            Projects may be suitable for carbon markets or Article 6 subject to host country authorization
            and independent validation. DeCarbonIQ provides governance infrastructure — not regulatory certification.
          </p>
        </div>
      </div>
    </section>
  );
}

// ── SECTION 7: FINAL CTA ──────────────────────────────────────────────────
function FinalCTA() {
  const navigate = useNavigate();
  return (
    <section className="bg-[#0F172A] py-24" data-testid="final-cta-section">
      <div className="px-6 text-center">
        <h2 className="text-5xl font-bold text-white tracking-tight mb-5">
          Start Your Climate Journey Today
        </h2>
        <p className="text-slate-400 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
          Join organisations across Africa and emerging markets building credible climate infrastructure.
        </p>
        <button onClick={() => navigate("/get-started")}
          className="inline-flex items-center gap-3 px-8 py-4 bg-[#059669] text-white text-base font-bold rounded-xl hover:bg-[#047857] transition-all shadow-lg shadow-[#059669]/25 mb-10"
          data-testid="final-start-btn">
          Start Now <ArrowRight size={18} />
        </button>

        {/* Final value props */}
        <div className="flex items-center justify-center gap-8 flex-wrap text-xs text-slate-500">
          {[
            "Measure your emissions",
            "Build your action plan",
            "Structure climate projects",
            "Unlock financing",
            "Prove your impact",
          ].map((item, i) => (
            <React.Fragment key={item}>
              <span>{item}</span>
              {i < 4 && <span className="text-slate-700">·</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── FOOTER ─────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="bg-[#080E1A] border-t border-white/5 py-12">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <img src={LOGO_URL} alt="DeCarbonIQ" className="h-6 mb-3 opacity-70" style={{ filter: "brightness(0) invert(1)" }} />
            <p className="text-xs text-slate-600 max-w-xs">
              Climate and circularity intelligence for SMEs, corporates, and financial institutions.
            </p>
          </div>
          <div className="flex items-center gap-6 text-xs text-slate-600">
            <Link to="/pricing" className="hover:text-slate-400 transition-colors">Pricing</Link>
            <Link to="/registry" className="hover:text-slate-400 transition-colors">Registry</Link>
            <Link to="/login" className="hover:text-slate-400 transition-colors">Sign in</Link>
            <Link to="/signup" className="hover:text-slate-400 transition-colors">Get started</Link>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-white/5 text-[10px] text-slate-700">
          DeCarbonIQ provides governance infrastructure — not regulatory certification, carbon market services, or financial advice.
        </div>
      </div>
    </footer>
  );
}

// ── MAIN PAGE ──────────────────────────────────────────────────────────────
export default function Home() {
  return (
    <div className="font-sans" data-testid="landing-page">
      <Nav />
      <Hero />
      <PlatformFlow />
      <WhoItsFor />
      <Capabilities />
      <PublicRegistry />
      <Article6Section />
      <FinalCTA />
      <Footer />
    </div>
  );
}
